import type { CSSProperties } from 'react';
import { escapeHtml } from './helpers';

export type FooterTemplate = 'standard' | 'complete' | 'minimal' | 'gradient' | 'social';

export const FOOTER_TEMPLATES: FooterTemplate[] = [
  'standard',
  'complete',
  'minimal',
  'gradient',
  'social',
];

const LEGACY_TEMPLATE_MAP: Record<string, FooterTemplate> = {
  colorful: 'social',
  split: 'gradient',
  vibrant: 'social',
  shopify: 'social',
  modern: 'standard',
};

export function normalizeFooterTemplate(value?: string): FooterTemplate {
  if (value && FOOTER_TEMPLATES.includes(value as FooterTemplate)) {
    return value as FooterTemplate;
  }
  if (value && LEGACY_TEMPLATE_MAP[value]) {
    return LEGACY_TEMPLATE_MAP[value];
  }
  return 'standard';
}

export function isFooterTemplate(value?: string): value is FooterTemplate {
  return FOOTER_TEMPLATES.includes(value as FooterTemplate);
}

export type FooterLink = { text: string; url?: string };

/** Footer blocks omit unsubscribe — global compliance footer handles it. */
export function filterFooterLinks(links?: FooterLink[]): FooterLink[] {
  return (links || []).filter((link) => !/^\s*unsubscribe(\s+link)?\s*$/i.test(String(link.text || '').trim()));
}

/** Target outer heights for footer palette thumbnails (px). */
export const FOOTER_PALETTE_PREVIEW_HEIGHTS: Record<string, number> = {
  'footer-standard': 72,
  'footer-complete': 118,
  'footer-minimal': 52,
  'footer-gradient': 96,
  'footer-social': 100,
};

const PALETTE_COMPACT: Record<FooterTemplate, { padding: string; fontSize: string }> = {
  standard: { padding: '14px 12px', fontSize: '9px' },
  complete: { padding: '12px 10px', fontSize: '8px' },
  minimal: { padding: '12px 10px', fontSize: '8px' },
  gradient: { padding: '14px 12px', fontSize: '9px' },
  social: { padding: '14px 12px', fontSize: '9px' },
};

export function linkRowJustifyFromAlign(ta: string): 'flex-start' | 'center' | 'flex-end' {
  return ta === 'left' ? 'flex-start' : ta === 'right' ? 'flex-end' : 'center';
}

export function linkAlignForEmail(ta: string): 'left' | 'center' | 'right' {
  return ta === 'left' ? 'left' : ta === 'right' ? 'right' : 'center';
}

export function resolveFooterStyles(
  content: Record<string, unknown>,
  styles: Record<string, unknown>,
  globalFontFamily?: string,
  isMobile = false,
  isPalettePreview = false
): Record<string, unknown> {
  const template = normalizeFooterTemplate(content.template as string | undefined);
  const merged: Record<string, unknown> = { ...styles };

  if (globalFontFamily && !merged.fontFamily) {
    merged.fontFamily = globalFontFamily;
  }

  if (isPalettePreview) {
    const compact = PALETTE_COMPACT[template];
    merged.padding = compact.padding;
    merged.fontSize = compact.fontSize;
  } else if (isMobile) {
    if (!merged.padding) {
      merged.padding = template === 'complete' ? '24px 16px' : '20px 16px';
    }
    if (!merged.fontSize) {
      merged.fontSize = '13px';
    }
    if (template === 'complete') {
      merged.textAlign = merged.textAlign || 'center';
    }
  }

  return merged;
}

export const footerStackMobile = (isMobile: boolean, isPalettePreview: boolean): boolean =>
  isMobile || isPalettePreview;

/** Email-safe social button (table-centered icon). */
export function footerSocialButtonHtml(
  href: string,
  iconInnerHtml: string,
  boxSize: number,
  bgColor: string,
  borderRadius: string | number,
  title = ''
): string {
  const radius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
  const px = `${boxSize}px`;
  const safeTitle = title ? ` title="${escapeHtml(title)}"` : '';
  return `<a href="${href}" class="footer-social-link"${safeTitle} style="display:inline-block;text-decoration:none;margin:0;vertical-align:middle;line-height:0;max-width:none !important;width:auto !important;">
    <table role="presentation" class="footer-social-btn" border="0" cellspacing="0" cellpadding="0" width="${boxSize}" style="width:${px} !important;height:${px} !important;min-width:${px} !important;max-width:${px} !important;min-height:${px} !important;max-height:${px} !important;table-layout:fixed !important;background-color:${bgColor};border-radius:${radius};border-collapse:collapse;margin:0;">
      <tr>
        <td class="footer-social-btn-cell" align="center" valign="middle" width="${boxSize}" height="${boxSize}" style="width:${px} !important;height:${px} !important;min-width:${px} !important;max-width:${px} !important;min-height:${px} !important;max-height:${px} !important;text-align:center !important;vertical-align:middle !important;line-height:0 !important;padding:0 !important;">
          <center style="line-height:0;">${iconInnerHtml}</center>
        </td>
      </tr>
    </table>
  </a>`;
}

export function footerInlineLinkHtml(text: string, url: string, extraStyle = ''): string {
  return `<a href="${url || '#'}" style="display:inline-block;white-space:nowrap;margin:0 10px;vertical-align:middle;text-decoration:none;${extraStyle}">${escapeHtml(text)}</a>`;
}

/** Builder: inline link row (non-social). */
export function footerLinksRowStyle(
  justify: 'flex-start' | 'center' | 'flex-end'
): CSSProperties {
  return {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    justifyContent: justify,
    flexWrap: 'wrap',
    rowGap: 10,
  };
}
